import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CheckCircle2 } from "lucide-react";
import { useWeb3Form } from "../../hooks/useWeb3Form";

const schema = z.object({
  name: z.string().trim().min(2, "Please enter your name."),
  email: z.string().trim().email("Please enter a valid email address."),
  message: z.string().trim().min(10, "Please write a short message (at least 10 characters)."),
  privacy: z.literal(true, {
    errorMap: () => ({ message: "Please acknowledge the privacy notice to continue." }),
  }),
});

const inputCls =
  "w-full bg-transparent border border-[#27272A] rounded-sm p-3 text-white placeholder-gray-500 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:outline-none";
const labelCls = "block text-sm uppercase tracking-[0.12em] font-semibold text-[#A1A1AA] mb-2";
const errCls = "mt-1.5 text-sm text-red-400";

export default function ContactForm() {
  const { status, errorMessage, submit } = useWeb3Form({
    subject: "Website Contact Enquiry",
    fromName: "Essex Black Cab website",
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: zodResolver(schema) });

  const onSubmit = async (values) => {
    await submit({
      "Name": values.name,
      "Message": values.message,
      email: values.email,
      replyto: values.email,
    });
  };

  if (status === "success") {
    return (
      <div data-testid="contact-confirmation" className="border border-[rgba(212,175,55,0.4)] bg-[#111111] p-8">
        <CheckCircle2 className="h-10 w-10 text-[#D4AF37]" aria-hidden="true" />
        <h3 className="mt-4 text-2xl font-semibold text-white">Message sent</h3>
        <p className="mt-3 leading-relaxed text-[#A1A1AA]">
          Thank you for getting in touch. We will reply to your enquiry as soon as possible.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate data-testid="contact-form" className="space-y-6">
      <input type="checkbox" name="botcheck" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

      <div>
        <label htmlFor="c-name" className={labelCls}>Name</label>
        <input id="c-name" data-testid="input-contact-name" autoComplete="name" className={inputCls} {...register("name")} />
        {errors.name && <p role="alert" className={errCls} data-testid="error-contact-name">{errors.name.message}</p>}
      </div>

      <div>
        <label htmlFor="c-email" className={labelCls}>Email address</label>
        <input id="c-email" data-testid="input-contact-email" type="email" autoComplete="email" className={inputCls} {...register("email")} />
        {errors.email && <p role="alert" className={errCls} data-testid="error-contact-email">{errors.email.message}</p>}
      </div>

      <div>
        <label htmlFor="c-message" className={labelCls}>Message</label>
        <textarea id="c-message" data-testid="input-contact-message" rows={6} className={inputCls} placeholder="How can we help?" {...register("message")} />
        {errors.message && <p role="alert" className={errCls} data-testid="error-contact-message">{errors.message.message}</p>}
      </div>

      <div>
        <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-[#A1A1AA]">
          <input type="checkbox" data-testid="input-contact-privacy" className="mt-1 h-4 w-4 shrink-0 accent-[#D4AF37]" {...register("privacy")} />
          <span>
            I have read and acknowledge the{" "}
            <a href="/privacy-policy" className="text-[#D4AF37] underline-offset-4 hover:underline">privacy notice</a>.
          </span>
        </label>
        {errors.privacy && <p role="alert" className={errCls} data-testid="error-contact-privacy">{errors.privacy.message}</p>}
      </div>

      {status === "error" && (
        <p role="alert" data-testid="contact-submit-error" className="border border-red-900 bg-red-950/40 p-4 text-sm text-red-300">
          {errorMessage}
        </p>
      )}

      <button
        type="submit"
        data-testid="contact-submit-button"
        disabled={status === "sending"}
        className="focus-gold w-full border border-[#D4AF37] bg-[#D4AF37] px-6 py-4 text-base font-semibold uppercase tracking-wider text-black transition-colors duration-150 hover:bg-[#E5C158] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        {status === "sending" ? "Sending…" : "Send message"}
      </button>
    </form>
  );
}
